import { ChangeDetectorRef, Component, ViewChild, ViewEncapsulation } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { PmDashboardService } from 'app/modules/well-integrity-planning/services/pm-dashboard.service';
import module from "app/modules/well-integrity-planning/well-integrity-planning.module";
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { takeUntil, merge, switchMap, EMPTY, map, Subject } from 'rxjs';

@Component({
    selector: 'app-field-summary',
    templateUrl: './string-summary.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: module,
    styleUrls: ['./string-summary.component.scss']
})

export class StringSummaryComponent {
    @ViewChild(MatPaginator) private _paginator: MatPaginator;
    @ViewChild(MatSort) private _sort: MatSort;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    dt: any;
    searchForm: UntypedFormGroup;
    startingIndex: number = 0
    isLoading: boolean = false

    constructor(
        private _formBuilder: UntypedFormBuilder,
        private pmDashboardService: PmDashboardService,
        private _changeDetectorRef: ChangeDetectorRef,
    ) { }

    ngOnInit(): void {
        this.setSearch();
        this.setDataTable();
        this.setPage({ offset: 0 });
    }

    ngAfterViewInit() {
        if (this._sort && this._paginator) {
            // Set the initial sort
            this._sort.sort({
                id: 'field',
                start: 'asc',
                disableClear: true,
            });

            // Mark for check
            this._changeDetectorRef.markForCheck();

            // If the user changes the sort order...
            this._sort.sortChange
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(() => {
                    // Reset back to the first page
                    this._paginator.pageIndex = 0;

                    this.onSort(this._sort)
                });

            // Get platform types if sort or page changes
            merge(this._sort.sortChange, this._paginator.page).pipe(
                switchMap(() => {
                    // this.isLoading = true;

                    let pageInfo = {
                        'count': this._paginator.length,
                        'pageSize': this._paginator.pageSize,
                        'limit': this._paginator.pageSize,
                        'offset': this._paginator.pageIndex
                    }
                    this.setPage(pageInfo);

                    return EMPTY
                }),
                map(() => {
                    this.isLoading = false;
                }),
            ).subscribe();
        }
    }

    ngOnDestroy() {
    }

    setSearch() {
        this.searchForm = this._formBuilder.group({
            Page: [0],
            PageSize: [10],
            OrderBy: [null],
            OrderDir: [null],
            WellName: [null],
            PlatformName: [null],
            StringName: [null],
            FieldName: [null],
            Year: [null],
        });

        this.searchForm.get('PageSize').valueChanges.subscribe(value => this.setSize(value));
    }

    setSize(size: number) {
        this.dt.page.size = size;
        this.dt.page.offset = 0
        this.setPage({ offset: this.dt.page.offset });
    }

    setDataTable() {
        this.dt = {
            page: {
                size: this.searchForm.get('PageSize')?.value,
                totalRecords: 0,
                totalPages: 0,
                offset: 0,
            },
            rows: [],
        };
    }

    setPage(pageInfo) {
        this.dt.page.offset = pageInfo.offset;
        this.startingIndex = this.dt.page.offset * this.dt.page.size

        this.searchForm.patchValue({
            Page: this.dt.page.offset + 1,
        });

        const params = this.searchForm.value;
        const search: any = {
            wellLongName: params.Well?.wellLongName,
            stringShortName: params.String?.stringShortName,
            platformLongName: params.Platform?.platformLongName,
            fieldLongName: params.Field?.fieldLongName,
        };

        this.pmDashboardService
            .getPmStringSummary({ ...params, search })
            .subscribe((pagedData) => {
                this.dt.page.totalPages = pagedData.data.totalPages;
                this.dt.page.totalRecords = pagedData.data.totalRecords;
                this.dt.page.size = pagedData.data.current;
                this.dt.rows = pagedData.data.data;
            });
    }

    onSort(sort: any) {
        if (!sort) return;
        this.searchForm.patchValue({
            OrderBy: sort.active,
            OrderDir: sort.direction,
        });

        this.setPage({ offset: 0 });
    }

    reset() {
        this.setSearch();
        this.setPage({ offset: 0 });
    }

    toggleExpandRow(row: any) {
        row.expanded = !row.expanded;
    }

    getStatusClass(status: string | null): string {
        switch (status) {
            case 'GOOD':
                return 'successBadge';
            case 'DEGRADED':
                return 'warningBadge';
            case 'FAIL':
            case 'FAILED':
                return 'failedBadge';
            case null:
            case 'NA':
            default:
                return 'nullBgColor';
        }
    }

    downloadStringsSummaryExcel() {
        this.pmDashboardService
            .getPmStringSummaryExport()
            .subscribe((blob) => {
                const { date, time } = this.getFormattedDateTime();
                const url = globalThis.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `PM_Strings_Summary_${date}_${time}.xlsx`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                globalThis.URL.revokeObjectURL(url);
            });
    }

    private getFormattedDateTime() {
        const now = new Date();
        const date = now.toISOString().split('T')[0]; // YYYY-MM-DD
        const time = now.toTimeString().split(' ')[0]; // HH:MM:SS
        return { date, time };
    }
}
